import { makeStyles } from "tss-react/mui"
import { useMatches, useNavigate } from "react-router-dom"
import { faChevronRight } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Divider, List, ListItemButton, Typography } from "@mui/material"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { UserType } from "../../login/loginTypes"
import { CardContentBox } from "./CardElements"
import {
    getEditCompanyAdminRoute,
    getEditMerchantAdminRoute,
    getEditServiceCenterAdminRoute,
    getEditSupplierAdminRoute,
    getEditTenantAdminRoute,
    getProfileChangePasswordRoute,
} from "../../../constants/routes"
import { isObjectWithProp } from "../../../lib/lang"
import { MatchEnvironment } from "../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../environment/envTypes"

const useStyles = makeStyles()((theme) => ({
    listItem: {
        padding: "1rem",
        justifyContent: "space-between",
    },
    textColor: {
        color: theme.palette.tailwind.gray[600],
    },
    deleteColor: {
        color: theme.palette.tailwind.red[600],
    },
    iconColor: {
        color: theme.palette.tailwind.neutral[600],
    },
}))

const editRouteMap: { [key in UserType]?: (userId: number) => string } = {
    [UserType.OFFICE_MANAGER]: getEditTenantAdminRoute,
    [UserType.MERCHANT]: getEditMerchantAdminRoute,
    [UserType.SUPPLIER]: getEditSupplierAdminRoute,
    [UserType.COMPANY]: getEditCompanyAdminRoute,
    [UserType.SERVICE_CENTER]: getEditServiceCenterAdminRoute,
}

type ActionsCardProps = {
    id: number
    type: UserType
}

export const ActionsCard = ({ id, type }: ActionsCardProps) => {
    const { classes } = useStyles()
    const navigate = useNavigate()
    const routeMatches = useMatches()
    const environmentRoutePath = routeMatches.find(
        (match) => isObjectWithProp(match.handle, "environmentRoot") && Boolean(match.handle.environmentRoot),
    )?.pathname

    const handleChangePassword = () => {
        navigate(getProfileChangePasswordRoute(environmentRoutePath))
    }

    const handleEdit = () => {
        const buildEditRoute = editRouteMap[type]
        if (buildEditRoute) {
            navigate(buildEditRoute(id))
        }
    }
    const handleDelete = () => {}

    return (
        <CardContentBox
            title={
                <LocalizedStrict id="profile-actions">
                    <>Shortcuts</>
                </LocalizedStrict>
            }
        >
            <List>
                <Divider />
                <MatchEnvironment environments={[EnvironmentType.Local, EnvironmentType.Test, EnvironmentType.Demo, EnvironmentType.QA]}>
                    <ListItemButton className={classes.listItem} onClick={handleChangePassword}>
                        <LocalizedStrict id="profile-actions-change-password">
                            <Typography variant="body1" className={classes.textColor}>
                                Change password
                            </Typography>
                        </LocalizedStrict>
                        <FontAwesomeIcon icon={faChevronRight} className={classes.iconColor} />
                    </ListItemButton>
                </MatchEnvironment>
                <Divider />
                <ListItemButton className={classes.listItem} onClick={handleEdit}>
                    <LocalizedStrict id="profile-actions-edit-profile">
                        <Typography variant="body1" className={classes.textColor}>
                            Edit profile
                        </Typography>
                    </LocalizedStrict>
                    <FontAwesomeIcon icon={faChevronRight} className={classes.iconColor} />
                </ListItemButton>
                <Divider />
                <ListItemButton className={classes.listItem} onClick={handleDelete} disabled>
                    <LocalizedStrict id="profile-actions-delete-account">
                        <Typography variant="body1" className={classes.deleteColor}>
                            Delete account
                        </Typography>
                    </LocalizedStrict>
                    <FontAwesomeIcon icon={faChevronRight} className={classes.iconColor} />
                </ListItemButton>
                <Divider />
            </List>
        </CardContentBox>
    )
}
